/**
 * nodeModel — per-node model/vendor derivation, shared by the graph and the
 * pipeline detail page.
 *
 * The model an agent runs on is chosen PER NODE, on the graph (each node that
 * runs a model carries a small "model" sub-node; clicking it picks the vendor +
 * model + key). This module is the ONE place that decides, from data alone,
 * what a node's model state IS — so the disc, the trigger gate and the
 * sub-agent check can never disagree.
 *
 * Storage (mirrors self-host exactly):
 *   • vendor + model → the workflow row's `nodeConfigOverrides[<nodeId>]`
 *     ({agent, model}); the runner folds them into the run's
 *     `config.models[<node>]` / `config.agents[<node>]`, which are the top of
 *     the engine's own resolution chains. The ENGINE is untouched.
 *   • the KEY → per VENDOR, never per node: the agent's own env (a snapshot
 *     the runtime reads FIRST) and, optionally, the project's shared
 *     credential.
 *
 * Nothing here names an agent, a slug or a template.
 */

// The env vars that carry a vendor's credential on an agent. First entry is
// what we WRITE; the rest are recognised when reading an existing snapshot.
export const VENDOR_ENV_KEYS = {
  claude: ['CLAUDE_CODE_OAUTH_TOKEN', 'ANTHROPIC_API_KEY', 'CLAUDE_CODE_CREDENTIALS_JSON'],
  codex: ['CODEX_API_KEY', 'OPENAI_API_KEY'],
  gemini: ['GEMINI_API_KEY', 'GOOGLE_API_KEY'],
};

export const VENDOR_LABELS = { claude: 'Claude', codex: 'Codex', gemini: 'Gemini' };

/** The node's serialized config from the graph (may be absent). */
export const nodeCfgOf = (graph, id) => {
  const c = graph?.nodeConfigs?.[id];
  return c && typeof c === 'object' ? c : null;
};

/** The node's code, however the serializer shipped it. */
const nodeCodeOf = (cfg) => {
  const s = cfg?.sourceCode || cfg?.executeCode || cfg?.customCode || '';
  return typeof s === 'string' ? s : '';
};

/**
 * Does this node invoke a model at all? A node with a prompt does; so does a
 * custom-execute node whose code calls invokeAgent. A SUB-GRAPH DISPATCH node
 * does NOT — it runs another agent, and that agent owns its own nodes' models
 * (set on the child's own page).
 */
export function nodeRunsModel(cfg) {
  if (!cfg) return false;
  if (typeof cfg.dispatchesWorkflow === 'string' && cfg.dispatchesWorkflow) return false;
  if (typeof cfg.prompt === 'string' && cfg.prompt) return true;
  return nodeCodeOf(cfg).indexOf('invokeAgent') !== -1;
}

// A custom-execute node can hand its own `model:` to invokeAgent (a cheap
// triage tier, say), and that beats the run's model — so the operator's pick
// would not take effect and the control must be read-only.
const INVOKE_MODEL_RE = /invokeAgent\s*\([\s\S]{0,400}?\bmodel\s*:/;
// …and when what it passes is a plain STRING LITERAL we can name it, so the
// disc says `haiku-4.5` instead of the useless "fixed". An expression
// (`resolveTriageModel(state)` — a fixed cheap TIER whose vendor follows the
// run) is unknowable from here, and we say so rather than guess.
const INVOKE_MODEL_LITERAL_RE = /invokeAgent\s*\([\s\S]{0,400}?\bmodel\s*:\s*['"]([^'"]{1,64})['"]/;

/**
 * Is this node's model FIXED BY THE TEMPLATE'S CODE?
 *
 * NOTE the `overrides` argument: the graph a detail page renders has the USER's
 * per-node overrides already MERGED into nodeConfigs (so the node editor shows
 * saved edits). Reading `cfg.agent` blind therefore reports the operator's OWN
 * pick as "fixed in code" — which renders the node locked and unclickable the
 * moment they set it. A vendor present in the override map came from the
 * operator and is not a lock.
 *
 * @returns {{vendor: string|null, model: boolean, modelName: string}|null}
 *          null when not fixed. `modelName` is '' unless the code names a
 *          literal model we can read off.
 */
export function nodeModelLock(cfg, override) {
  if (!cfg) return null;
  const userPickedVendor = typeof override?.agent === 'string' && override.agent.trim();
  const vendor = !userPickedVendor && typeof cfg.agent === 'string' && cfg.agent.trim()
    ? cfg.agent.trim()
    : null;
  let codeModel = false;
  let modelName = '';
  try {
    const code = nodeCodeOf(cfg);
    codeModel = INVOKE_MODEL_RE.test(code);
    if (codeModel) {
      const m = code.match(INVOKE_MODEL_LITERAL_RE);
      // 'auto' names nothing — it IS the inherit case, so it would be a lie on
      // a disc that claims the model is fixed.
      if (m && m[1] && m[1] !== 'auto') modelName = m[1];
    }
  } catch { codeModel = false; }
  if (!vendor && !codeModel) return null;
  return { vendor, model: codeModel, modelName };
}

/** This node's saved pick, as {agent?, model?} ({} when it inherits). */
export function nodeModelPick(override) {
  const out = {};
  if (typeof override?.agent === 'string' && override.agent.trim()) out.agent = override.agent.trim();
  if (typeof override?.model === 'string' && override.model.trim()) out.model = override.model.trim();
  return out;
}

/**
 * Does a credential exist for `vendor`?
 *
 * The AGENT'S OWN ENV is checked FIRST and is sufficient on its own — that is
 * what the RUNTIME does (agent env beats the project credential). Judging by
 * the project alone marks a node red the moment the page reloads after saving
 * a key to the agent, and again whenever someone removes a project credential
 * the agent had stopped depending on.
 *
 * @param {object} agentEnvKeys  {ENV_NAME: lastFour|true} — the agent's env
 * @param {object} projectSecrets the project's masked secrets
 * @returns {boolean} — true when unknown (never cry wolf)
 */
export function vendorHasKey(vendor, agentEnvKeys, projectSecrets, projectVendor) {
  if (!vendor) return true;
  if (agentEnvKeys) {
    const names = VENDOR_ENV_KEYS[vendor] || [];
    for (const n of names) if (agentEnvKeys[n]) return true;
  }
  if (!projectSecrets && !agentEnvKeys) return true; // nothing loaded yet
  if (!projectSecrets) return false;
  return !!projectSecretFor(vendor, projectSecrets, projectVendor);
}

/** The project's (masked) credential for a vendor, or ''. */
export function projectSecretFor(vendor, secrets, projectVendor) {
  const s = secrets || {};
  const str = (x) => (typeof x === 'string' && x ? x : '');
  const isPrimary = vendor === projectVendor;
  if (vendor === 'codex') return str(s.codexApiKey) || (isPrimary ? str(s.aiApiKey) : '');
  if (vendor === 'gemini') return str(s.geminiApiKey) || (isPrimary ? str(s.aiApiKey) : '');
  return str(s.aiOAuthToken) || str(s.aiApiKey); // claude
}

/** The agent's own snapshot for a vendor: its lastFour, `true`, or ''. */
export function agentSnapshotFor(vendor, agentEnvKeys) {
  const names = VENDOR_ENV_KEYS[vendor] || [];
  for (const n of names) {
    const hit = agentEnvKeys?.[n];
    if (hit) return hit;
  }
  return '';
}

/**
 * The model a code-LOCKED node will actually run, when the template DECLARES it.
 *
 * A node whose code picks its own model usually picks a cheap TIER per vendor
 * (`FAST_TRIAGE_MODEL_BY_VENDOR[resolveRunVendor(state)]`), so the concrete
 * alias is unknowable from the graph: what a deployed row carries is
 * `customExecute.toString()` — the function's OWN text, not the module around
 * it, so the map and the resolver it calls are simply absent.
 *
 * We don't guess at it. The template already publishes the answer as DATA: a
 * `models[]` block with `fixedModelByVendor` (the SAME frozen map the node asks
 * the vendor for, so the two cannot drift). Join it to the node by `role`.
 *
 * @param {object[]} modelBlocks  marketplace.models
 * @returns {string} the alias, or '' when nothing is declared for this node
 */
export function declaredFixedModel(modelBlocks, nodeId, vendor) {
  if (!Array.isArray(modelBlocks) || !nodeId || !vendor) return '';
  const block = modelBlocks.find((b) => b && b.role === nodeId);
  const byVendor = block?.fixedModelByVendor;
  if (!byVendor || typeof byVendor !== 'object') return '';
  const m = byVendor[vendor];
  return typeof m === 'string' && m && m !== 'auto' ? m : '';
}

/**
 * One node's model STATE — the single source the disc, the trigger gate and
 * the sub-agent check all read.
 *
 * @returns {{kind:'locked'|'ready'|'nokey'|'unset', vendor:string, label:string, sub:string}}
 */
export function nodeModelState({
  cfg, nodeId, override, agentEnvKeys, projectSecrets, projectVendor, modelBlocks,
}) {
  const lock = nodeModelLock(cfg, override);
  if (lock) {
    // WHICH vendor this node actually runs on. Code that fixes the model
    // usually fixes a cheap TIER, not a vendor (`FAST_TRIAGE_MODEL_BY_VENDOR[
    // resolveRunVendor(state)]`) — so the vendor is still inherited, and the
    // disc must show the one that will really be used: the template's pin, the
    // operator's own pick, then the project default. A generic glyph told the
    // operator nothing about whether this node bills Anthropic or OpenAI.
    const vendor = lock.vendor
      || (typeof override?.agent === 'string' ? override.agent.trim() : '')
      || projectVendor
      || '';
    // Say the most specific TRUE thing: a literal the code names, then the
    // model the TEMPLATE declares for this node + vendor, then just "Fixed".
    // The padlock already carries "you can't change this" — the caption
    // shouldn't spend its one line repeating it.
    const declared = declaredFixedModel(modelBlocks, nodeId, vendor);
    // A fixed MODEL still needs a KEY. The template chose which model runs
    // here; it cannot supply the credential to run it — so a locked node with
    // no credential for its vendor is exactly as dead as an unset one, and has
    // to say so and be clickable. (It was neither: read-only and skipped by the
    // trigger check, which left no way at all to give it a key.)
    const missingKey = !vendorHasKey(vendor, agentEnvKeys, projectSecrets, projectVendor);
    return {
      kind: 'locked',
      vendor,
      label: lock.modelName
        || declared
        || (lock.model ? 'Fixed' : (VENDOR_LABELS[vendor] || vendor || 'Fixed')),
      sub: missingKey ? `no ${VENDOR_LABELS[vendor] || vendor} key` : '',
      missingKey,
    };
  }
  const pick = nodeModelPick(override);
  if (pick.agent || pick.model) {
    const vendor = pick.agent || '';
    const label = pick.model && pick.model !== 'auto'
      ? pick.model
      : (VENDOR_LABELS[vendor] || vendor);
    if (!vendorHasKey(vendor, agentEnvKeys, projectSecrets, projectVendor)) {
      return { kind: 'nokey', vendor, label, sub: `no ${VENDOR_LABELS[vendor] || vendor} key` };
    }
    return { kind: 'ready', vendor, label, sub: '' };
  }
  return { kind: 'unset', vendor: '', label: 'Set model', sub: '' };
}

/**
 * Nodes of this graph that cannot run yet: no model picked, or a model whose
 * vendor has no credential anywhere.
 *
 * A code-LOCKED node is NOT ready by construction — the template fixed which
 * model runs, not who pays for it, so one whose vendor has no key fails the
 * run exactly like an unset node. Skipping them here is what made a fixed node
 * a dead end: unclickable AND never flagged.
 */
export function unreadyNodes({
  graph, overrides, agentEnvKeys, projectSecrets, projectVendor, modelBlocks,
}) {
  const out = [];
  for (const n of (graph?.nodes || [])) {
    const cfg = nodeCfgOf(graph, n.id);
    if (!nodeRunsModel(cfg)) continue;
    const st = nodeModelState({
      cfg,
      nodeId: n.id,
      override: overrides?.[n.id],
      agentEnvKeys,
      projectSecrets,
      projectVendor,
      modelBlocks,
    });
    if (st.kind === 'unset') out.push({ id: n.id, why: 'no model' });
    else if (st.kind === 'nokey' || st.missingKey) out.push({ id: n.id, why: 'no key' });
  }
  return out;
}
